import { useEffect, useState } from 'react';
import axios from '../../api/axios';
import { toast } from 'react-toastify';

const inputStyle = {
  width: '100%', padding: '10px 12px', border: '1px solid #ddd',
  borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box'
};
const labelStyle = { display: 'block', marginBottom: '6px', fontWeight: '600', color: '#444' };
const cardStyle  = { background: 'white', padding: '24px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' };

const ROLE_CONFIG = {
  invigilator:  { label: 'Invigilator',  color: '#1565c0', bg: '#e3f2fd', border: '#90caf9' },
  tca:          { label: 'TCA',          color: '#6a1b9a', bg: '#f3e5f5', border: '#ce93d8' },
  system_admin: { label: 'System Admin', color: '#e65100', bg: '#fff3e0', border: '#ffcc80' },
  attender:     { label: 'Attender',     color: '#2e7d32', bg: '#e8f5e9', border: '#a5d6a7' },
};

const PRIORITY_CONFIG = {
  available:     { label: 'Available',      color: '#2e7d32', bg: '#e8f5e9', border: '#a5d6a7' },
  same_schedule: { label: 'Same Schedule',  color: '#e65100', bg: '#fff3e0', border: '#ffcc80' },
  same_day:      { label: 'Assigned Today', color: '#b71c1c', bg: '#ffebee', border: '#ef9a9a' },
};

const normalizeId = (val) => (val === null || val === undefined) ? '' : String(val).trim();

const extractUserId = (m) => normalizeId(
  m.user_id ?? m.userId ?? m.faculty_id ?? m.invigilator_id ?? m.id ?? null
);

// ✅ Pure string slicing — no Date object parsing, so no timezone drift.
// Works whether the backend sends '2026-07-20' or '2026-07-20T00:00:00.000Z'.
const toDateOnly = (val) => (val ? String(val).slice(0, 10) : '');

const formatShiftDate = (dateStr) => {
  const d = toDateOnly(dateStr);
  if (!d) return '';
  const date = new Date(d + 'T00:00:00');
  return isNaN(date.getTime()) ? dateStr : date.toLocaleDateString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric'
  });
};

const ROLE_ORDER = ['invigilator', 'tca', 'system_admin', 'attender'];

const InvigilatorMapping = () => {
  const [loading, setLoading]                       = useState(false);
  const [schedules, setSchedules]                   = useState([]);
  const [shifts, setShifts]                         = useState([]);
  const [filteredShifts, setFilteredShifts]         = useState([]);
  const [allUsers, setAllUsers]                     = useState([]);
  const [usersWithPriority, setUsersWithPriority]   = useState([]);
  const [selectedSchedule, setSelectedSchedule]     = useState('');
  const [selectedShift, setSelectedShift]           = useState('');
  const [selectedShiftData, setSelectedShiftData]   = useState(null);
  const [selectedUsers, setSelectedUsers]           = useState([]);
  const [alreadyAssigned, setAlreadyAssigned]       = useState([]);
  const [loadingStatus, setLoadingStatus]           = useState(false);
  const [sameDayShifts, setSameDayShifts]           = useState([]);

  const [requiredCounts, setRequiredCounts] = useState({
    invigilator: 1, tca: 0, system_admin: 0, attender: 0
  });

  useEffect(() => {
    fetchSchedules();
    fetchShifts();
    fetchUsers();
  }, []);

  const fetchSchedules = async () => {
    try { const r = await axios.get('/schedules'); setSchedules(r.data || []); }
    catch { toast.error('Failed to load schedules'); }
  };

  const fetchShifts = async () => {
    try { const r = await axios.get('/shifts'); setShifts(r.data || []); }
    catch { toast.error('Failed to load shifts'); }
  };

  const fetchUsers = async () => {
    try {
      const r = await axios.get('/users');
      const filtered = (r.data || []).filter(u =>
        ['invigilator', 'tca', 'system_admin', 'attender', 'faculty'].includes(u.role_name || u.role)
      );
      setAllUsers(filtered);
    } catch { toast.error('Failed to load users'); }
  };

  const selectedScheduleData = schedules.find(s => normalizeId(s.id) === normalizeId(selectedSchedule));
  const remunerationType = selectedScheduleData?.remuneration_type || 'per_shift';

  const calcRequiredFromStudents = (studentCount) => {
    const count = Number(studentCount || 0);
    return {
      invigilator:  Math.max(1, Math.ceil(count / 30)),
      tca:          count > 0 ? 1 : 0,
      system_admin: 0,
      attender:     count > 60 ? 1 : 0,
    };
  };

  const handleScheduleChange = (scheduleId) => {
    setSelectedSchedule(scheduleId);
    setSelectedShift('');
    setSelectedShiftData(null);
    setSelectedUsers([]);
    setAlreadyAssigned([]);
    setUsersWithPriority([]);
    setSameDayShifts([]);
    setFilteredShifts(!scheduleId ? [] :
      shifts.filter(s => normalizeId(s.schedule_id) === normalizeId(scheduleId))
    );
  };

  const handleShiftChange = async (shiftId) => {
    setSelectedShift(shiftId);
    setSelectedUsers([]);
    setAlreadyAssigned([]);
    setUsersWithPriority([]);
    setSameDayShifts([]);
    if (!shiftId || !selectedSchedule) return;

    const shiftData = shifts.find(s => normalizeId(s.id) === normalizeId(shiftId));
    setSelectedShiftData(shiftData);

    if (shiftData) {
      setRequiredCounts(calcRequiredFromStudents(shiftData.student_count));
    }

    const currentShiftDate = toDateOnly(shiftData?.shift_date);

    // ✅ For per_day exams, show which other shifts on this EXACT date
    // (same schedule only) will be affected by this assignment.
    if (shiftData && remunerationType === 'per_day') {
      const others = shifts.filter(s =>
        normalizeId(s.schedule_id) === normalizeId(selectedSchedule) &&
        toDateOnly(s.shift_date) === currentShiftDate &&
        normalizeId(s.id) !== normalizeId(shiftId)
      );
      setSameDayShifts(others);
    }

    setLoadingStatus(true);
    try {
      let assignedToThisShift = [];
      try {
        const r = await axios.get(`/invigilators/invigilator-mapping/${shiftId}`);
        assignedToThisShift = r.data || [];
      } catch (_) {}

      const assignedIdSet = new Set(assignedToThisShift.map(extractUserId).filter(Boolean));
      setAlreadyAssigned(assignedToThisShift);

      // ✅ "Same day" = same exact calendar date string, checked across ALL
      // schedules (a person can't be in two places on the same real day).
      const shiftsOnSameDay = shifts.filter(s => {
        if (normalizeId(s.id) === normalizeId(shiftId)) return false;
        const sDate = toDateOnly(s.shift_date);
        return sDate && currentShiftDate && sDate === currentShiftDate;
      });

      const shiftsInSameSchedule = shifts.filter(s =>
        normalizeId(s.schedule_id) === normalizeId(selectedSchedule) &&
        normalizeId(s.id) !== normalizeId(shiftId)
      );

      const allRelevantIds = [...new Set([
        ...shiftsOnSameDay.map(s => s.id),
        ...shiftsInSameSchedule.map(s => s.id)
      ])];

      const mappingResults = await Promise.allSettled(
        allRelevantIds.map(sid => axios.get(`/invigilators/invigilator-mapping/${sid}`))
      );

      const assignedSameDayIds      = new Set();
      const assignedSameScheduleIds = new Set();

      mappingResults.forEach((result, idx) => {
        if (result.status === 'fulfilled') {
          const mappings = result.value.data || [];
          const sid = normalizeId(allRelevantIds[idx]);
          mappings.forEach(m => {
            const uid = extractUserId(m);
            if (uid) {
              if (shiftsOnSameDay.some(s => normalizeId(s.id) === sid))      assignedSameDayIds.add(uid);
              if (shiftsInSameSchedule.some(s => normalizeId(s.id) === sid)) assignedSameScheduleIds.add(uid);
            }
          });
        }
      });

      const enriched = allUsers
        .filter(f => !assignedIdSet.has(normalizeId(f.id)))
        .map(f => {
          const fid = normalizeId(f.id);
          let priority = 'available';
          if (assignedSameDayIds.has(fid))      priority = 'same_day';
          else if (assignedSameScheduleIds.has(fid)) priority = 'same_schedule';
          return { ...f, priority };
        });

      const order = { available: 0, same_schedule: 1, same_day: 2 };
      enriched.sort((a, b) => order[a.priority] - order[b.priority]);
      setUsersWithPriority(enriched);

    } catch (err) {
      toast.error('Failed to compute availability');
      setUsersWithPriority(allUsers.map(f => ({ ...f, priority: 'available' })));
    } finally {
      setLoadingStatus(false);
    }
  };

  const getRoleRate = (role) => {
    if (!selectedShiftData) return 0;
    const map = {
      invigilator: selectedShiftData.rate_invigilator,
      tca: selectedShiftData.rate_tca,
      system_admin: selectedShiftData.rate_system_admin,
      attender: selectedShiftData.rate_attender,
    };
    return Number(map[role] || selectedShiftData.remuneration_rate || 0);
  };

  const totalRequired = Object.values(requiredCounts).reduce((a, b) => a + b, 0);

  const getSelectedCountForRole = (role) =>
    selectedUsers.filter(id => {
      const u = usersWithPriority.find(x => normalizeId(x.id) === normalizeId(id));
      return (u?.role_name || u?.role) === role;
    }).length;

  const handleUserSelect = (userId) => {
    const user = usersWithPriority.find(f => normalizeId(f.id) === normalizeId(userId));
    const userRole = user?.role_name || user?.role;
    const alreadySelected = selectedUsers.map(normalizeId).includes(normalizeId(userId));

    if (alreadySelected) {
      setSelectedUsers(selectedUsers.filter(id => normalizeId(id) !== normalizeId(userId)));
      return;
    }

    const roleCount    = getSelectedCountForRole(userRole);
    const roleRequired = requiredCounts[userRole] || 0;

    if (roleCount >= roleRequired) {
      toast.warning(`Only ${roleRequired} ${ROLE_CONFIG[userRole]?.label || userRole}(s) required`);
      return;
    }

    if (user?.priority === 'same_day') {
      toast.warning(`⚠️ ${user.name} is already on duty today.`);
    }

    setSelectedUsers([...selectedUsers, userId]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedSchedule) { toast.error('Please select a schedule'); return; }
    if (!selectedShift)    { toast.error('Please select a shift');    return; }
    if (selectedUsers.length === 0) { toast.error('Please select at least one person'); return; }

    for (const role of ROLE_ORDER) {
      const required = requiredCounts[role] || 0;
      const selected = getSelectedCountForRole(role);
      if (required > 0 && selected < required) {
        toast.warning(`Please select ${required} ${ROLE_CONFIG[role]?.label}(s). Currently: ${selected}`);
        return;
      }
    }

    try {
      setLoading(true);
      const res = await axios.post('/invigilators/invigilator-mapping', {
        shift_id: Number(selectedShift),
        invigilator_ids: selectedUsers
      });
      toast.success(res.data?.message || 'Assigned successfully!');
      setSelectedUsers([]);
      setSelectedShift('');
      setSelectedSchedule('');
      setFilteredShifts([]);
      setUsersWithPriority([]);
      setAlreadyAssigned([]);
      setSelectedShiftData(null);
      setSameDayShifts([]);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to assign');
    } finally {
      setLoading(false);
    }
  };

  const renderUserCard = (user) => {
    const isSelected  = selectedUsers.map(normalizeId).includes(normalizeId(user.id));
    const priCfg      = PRIORITY_CONFIG[user.priority] || PRIORITY_CONFIG.available;
    const userRole    = user.role_name || user.role;
    const roleCfg     = ROLE_CONFIG[userRole] || { label: userRole, color: '#555', bg: '#f5f5f5', border: '#ddd' };

    return (
      <div key={user.id} onClick={() => handleUserSelect(user.id)} style={{
        border: isSelected ? '2px solid #1a237e' : `1px solid ${priCfg.border}`,
        borderRadius: '10px', padding: '14px 16px', cursor: 'pointer',
        background: isSelected ? '#eef2ff' : priCfg.bg,
        transition: '0.2s', position: 'relative',
        opacity: user.priority === 'same_day' && !isSelected ? 0.75 : 1
      }}>
        {isSelected && (
          <span style={{
            position: 'absolute', top: '10px', right: '12px',
            background: '#1a237e', color: 'white', borderRadius: '50%',
            width: '20px', height: '20px', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            fontSize: '12px', fontWeight: 'bold'
          }}>✓</span>
        )}
        <h4 style={{ margin: '0 0 4px 0', color: '#1a237e', paddingRight: '28px' }}>{user.name}</h4>
        <p style={{ margin: 0, color: '#666', fontSize: '13px' }}>{user.email}</p>
        <div style={{ marginTop: '8px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          <span style={{
            fontSize: '11px', fontWeight: 600, color: roleCfg.color,
            background: roleCfg.bg, border: `1px solid ${roleCfg.border}`,
            borderRadius: '20px', padding: '2px 8px'
          }}>{roleCfg.label}</span>
          <span style={{
            fontSize: '11px', fontWeight: 600, color: priCfg.color,
            background: 'white', border: `1px solid ${priCfg.border}`,
            borderRadius: '20px', padding: '2px 8px'
          }}>{priCfg.label}</span>
          {selectedShiftData && (
            <span style={{
              fontSize: '11px', fontWeight: 600, color: '#2e7d32',
              background: '#e8f5e9', borderRadius: '20px', padding: '2px 8px'
            }}>₹{getRoleRate(userRole).toFixed(2)}/shift</span>
          )}
        </div>
      </div>
    );
  };

  const renderRoleGroup = (role) => {
    const cfg   = ROLE_CONFIG[role] || { label: role, color: '#555' };
    const group = usersWithPriority.filter(u => (u.role_name || u.role) === role);
    if (group.length === 0) return null;
    const selected  = getSelectedCountForRole(role);
    const required  = requiredCounts[role] || 0;
    const rate      = getRoleRate(role);

    return (
      <div key={role} style={{ marginBottom: '28px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
          <span style={{
            fontSize: '13px', fontWeight: 700, color: cfg.color,
            textTransform: 'uppercase', letterSpacing: '0.5px'
          }}>
            {cfg.label}s ({group.length} available)
          </span>
          <span style={{
            fontSize: '12px', fontWeight: 600, padding: '2px 10px',
            borderRadius: '20px', background: selected >= required && required > 0 ? '#e8f5e9' : '#fff8e1',
            color: selected >= required && required > 0 ? '#2e7d32' : '#f57f17'
          }}>
            {selected}/{required} selected
          </span>
          {rate > 0 && (
            <span style={{ fontSize: '12px', color: '#2e7d32', fontWeight: 600 }}>
              ₹{rate.toFixed(2)}/shift
            </span>
          )}
          <div style={{ flex: 1, height: '1px', background: '#e0e0e0' }} />
        </div>
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '14px'
        }}>
          {group.map(renderUserCard)}
        </div>
      </div>
    );
  };

  return (
    <div style={{ padding: '32px', background: '#f5f6fa', minHeight: '100vh' }}>
      <div style={{ marginBottom: '24px' }}>
        <h2 style={{ color: '#1a237e', marginBottom: '8px' }}>Invigilator Mapping</h2>
        <p style={{ color: '#666' }}>Assign staff to exam shifts by role</p>
      </div>

      <div style={cardStyle}>
        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>

            <div>
              <label style={labelStyle}>Select Schedule *</label>
              <select value={selectedSchedule} onChange={e => handleScheduleChange(e.target.value)}
                required style={{ ...inputStyle, backgroundColor: 'white' }}>
                <option value="">-- Select Schedule --</option>
                {schedules.map(s => <option key={s.id} value={s.id}>{s.exam_name}</option>)}
              </select>
              {selectedSchedule && (
                <span style={{
                  display: 'inline-block', marginTop: '6px', fontSize: '11px', fontWeight: 700,
                  padding: '2px 9px', borderRadius: '20px',
                  background: remunerationType === 'per_day' ? '#fff8e1' : '#eef2ff',
                  color: remunerationType === 'per_day' ? '#f57f17' : '#1a237e'
                }}>
                  {remunerationType === 'per_day' ? 'Per Day' : 'Per Shift'}
                </span>
              )}
            </div>

            <div>
              <label style={labelStyle}>Select Shift *</label>
              <select value={selectedShift} onChange={e => handleShiftChange(e.target.value)}
                required disabled={!selectedSchedule}
                style={{ ...inputStyle, backgroundColor: selectedSchedule ? 'white' : '#f0f0f0' }}>
                <option value="">{selectedSchedule ? '-- Select Shift --' : 'Select a schedule first'}</option>
                {filteredShifts.map(shift => (
                  <option key={shift.id} value={shift.id}>
                    {shift.shift_name} — {formatShiftDate(shift.shift_date)}
                    {shift.start_time ? ` (${shift.start_time} - ${shift.end_time})` : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {remunerationType === 'per_day' && sameDayShifts.length > 0 && (
            <div style={{
              marginTop: '16px', padding: '12px 16px', background: '#fff8e1',
              borderRadius: '8px', border: '1px solid #ffe082', fontSize: '13px', color: '#f57f17'
            }}>
              <strong>Per Day exam:</strong> the staff you select below will be assigned to this shift AND
              automatically to: {sameDayShifts.map(s => s.shift_name).join(', ')} — same team, whole day.
            </div>
          )}

          {selectedShiftData && (
            <div style={{
              marginTop: '20px', padding: '16px', background: '#f0f4ff',
              borderRadius: '10px', border: '1px solid #c5cae9'
            }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '24px', marginBottom: '16px' }}>
                <div>
                  <span style={{ fontSize: '12px', color: '#888' }}>Students</span>
                  <div style={{ fontWeight: 700, color: '#1a237e' }}>{selectedShiftData.student_count || 0}</div>
                </div>
                {ROLE_ORDER.map(role => (
                  <div key={role}>
                    <span style={{ fontSize: '12px', color: '#888' }}>{ROLE_CONFIG[role]?.label} Rate</span>
                    <div style={{ fontWeight: 700, color: '#2e7d32' }}>₹{getRoleRate(role).toFixed(2)}</div>
                  </div>
                ))}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '12px' }}>
                {ROLE_ORDER.map(role => (
                  <div key={role}>
                    <label style={{ fontSize: '12px', fontWeight: 600, color: ROLE_CONFIG[role]?.color }}>
                      {ROLE_CONFIG[role]?.label} Required
                    </label>
                    <input type="number" min="0" value={requiredCounts[role]}
                      onChange={e => setRequiredCounts(prev => ({ ...prev, [role]: Number(e.target.value) }))}
                      style={{ ...inputStyle, marginTop: '4px' }} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {selectedShift && alreadyAssigned.length > 0 && (
            <div style={{
              marginTop: '16px', padding: '12px 16px', background: '#fff3e0',
              borderRadius: '8px', border: '1px solid #ffe0b2', fontSize: '13px', color: '#e65100'
            }}>
              <strong>Already assigned:</strong>{' '}
              {alreadyAssigned.map(a => `${a.name} (${a.role_name})`).join(', ')}
              {' '}— hidden to prevent duplicates.
            </div>
          )}

          <div style={{ marginTop: '30px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <label style={{ ...labelStyle, margin: 0 }}>
                Select Staff ({selectedUsers.length}/{totalRequired} selected)
              </label>
            </div>

            {!selectedShift ? (
              <p style={{ color: '#999', fontStyle: 'italic' }}>Please select a schedule and shift first.</p>
            ) : loadingStatus ? (
              <p style={{ color: '#999', fontStyle: 'italic' }}>Checking availability...</p>
            ) : usersWithPriority.length === 0 ? (
              <p style={{ color: '#e53935', fontStyle: 'italic' }}>All staff are already assigned to this shift.</p>
            ) : (
              ROLE_ORDER.map(role => renderRoleGroup(role))
            )}
          </div>

          {selectedShift && selectedUsers.length > 0 && (
            <div style={{
              marginTop: '24px', padding: '16px', background: '#eef2ff',
              borderRadius: '10px', border: '1px solid #c5cae9'
            }}>
              <strong>Selected:</strong> {selectedUsers.length} / {totalRequired}
              <div style={{ marginTop: '8px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {selectedUsers.map(id => {
                  const u = usersWithPriority.find(x => normalizeId(x.id) === normalizeId(id));
                  const role = u?.role_name || u?.role;
                  const cfg  = ROLE_CONFIG[role] || { bg: '#f5f5f5', border: '#ddd', color: '#555' };
                  return (
                    <span key={id} style={{
                      fontSize: '12px', padding: '4px 10px', borderRadius: '20px',
                      background: cfg.bg, border: `1px solid ${cfg.border}`, color: cfg.color, fontWeight: 600
                    }}>
                      {u?.name} ({cfg.label || role})
                      <span onClick={() => setSelectedUsers(selectedUsers.filter(x => normalizeId(x) !== normalizeId(id)))}
                        style={{ marginLeft: '6px', cursor: 'pointer', fontWeight: 'bold' }}>×</span>
                    </span>
                  );
                })}
              </div>
            </div>
          )}

          <button type="submit" disabled={loading || selectedUsers.length === 0} style={{
            marginTop: '30px', width: '100%', padding: '14px',
            background: loading || selectedUsers.length === 0 ? '#9fa8da' : '#1a237e',
            color: 'white', border: 'none', borderRadius: '10px',
            fontSize: '16px', fontWeight: '600',
            cursor: loading || selectedUsers.length === 0 ? 'not-allowed' : 'pointer'
          }}>
            {loading ? 'Assigning...' : 'Assign Staff'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default InvigilatorMapping;